/**
 * The tab row — §3.3 of the record page plan.
 *
 * 11px/12px padding, 13.5px, inactive 500 muted, active 600 with a 2px accent
 * rule pulled down over the row's own border. An icon to the left of every
 * label, and a count pill to the right when there is a number worth knowing
 * before you open it.
 *
 * Two behaviours that are not visible in a screenshot:
 *
 * **It scrolls sideways on an iPad** rather than wrapping — a second row of
 * tabs moves the content down and makes the header a different height on
 * different records.
 *
 * **The active tab is scrolled into view on mount**, because a link to
 * `?tab=history` on a narrow screen otherwise opens a page whose current tab
 * is off the right-hand edge.
 */
import { useEffect, useRef } from 'react'
import { cn } from '../lib/cn.js'
import { COLUMN_ICONS, NAV_ICONS, RECORD_TAB_ICONS } from '../frame/icons.js'
import type { FieldIcon } from '@rowted/shared'

export interface RecordTab {
  key: string
  label: string
  /** Named from the same two sets the rest of the app draws from. */
  icon?: FieldIcon | keyof typeof NAV_ICONS | keyof typeof RECORD_TAB_ICONS
  count?: number
  /**
   * The tab takes the whole width; the rail steps aside for it.
   *
   * For a tab that is the full list engine. A list with a search box, a
   * filter, six columns and a pager does not fit beside a 340px rail, and the
   * rail is answering a question — "what is this record" — that the header
   * above has already answered for somebody who came here to read a table.
   */
  wide?: boolean
}

export function RecordTabs({
  tabs, active, onChange, tabletGutter = false,
}: {
  tabs: RecordTab[]
  active: string
  onChange: (key: string) => void
  /**
   * Folder 50's iPad tab row — 20 px either side below 1280 (Board 15), with
   * `RecordConfig.tabletGutters: 'wide'`. Opt-in; left out, today's 24.
   */
  tabletGutter?: boolean
}) {
  const row = useRef<HTMLDivElement>(null)

  useEffect(() => {
    row.current?.querySelector<HTMLElement>('[aria-selected="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [active])

  return (
    <div
      ref={row}
      role="tablist"
      // `no-scrollbar`: the bar would sit on the border and read as a second
      // rule. One class, because the two inline selectors it replaces are the
      // same rule spelled twice.
      className={cn(
        'no-scrollbar flex shrink-0 items-stretch gap-1.5 overflow-x-auto',
        'border-b border-line bg-surface',
        tabletGutter ? 'px-5 xl:px-6' : 'px-6',
      )}
    >
      {tabs.map((tab) => {
        const Icon = iconFor(tab.icon)
        const on = tab.key === active
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(tab.key)}
            className={cn(
              'relative flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-[11px]',
              'text-[13.5px]',
              on ? 'font-semibold text-text' : 'font-medium text-text-muted hover:text-text',
            )}
          >
            {Icon && (
              <span className={on ? 'text-accent' : 'text-text-faint'}>
                <Icon size={16} />
              </span>
            )}
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="tnum rounded-chip bg-line-soft px-[7px] py-px text-[11px] font-semibold text-text-muted">
                {tab.count}
              </span>
            )}
            {/* Pulled 1px down so it sits over the row's border, not above it. */}
            {on && <span aria-hidden className="absolute inset-x-0 -bottom-px h-0.5 bg-accent" />}
          </button>
        )
      })}
    </div>
  )
}

/** A tab's icon, from whichever of the two icon sets names it. */
function iconFor(name: RecordTab['icon']) {
  if (!name) return null
  if (name in RECORD_TAB_ICONS) return RECORD_TAB_ICONS[name as keyof typeof RECORD_TAB_ICONS]
  if (name in NAV_ICONS) return NAV_ICONS[name as keyof typeof NAV_ICONS]
  return COLUMN_ICONS[name as FieldIcon] ?? null
}
